import {
  checkForUpdate,
  isUpdateReady,
  activateUpdateAndReload,
  verifyFreshSchedule,
  hasOfflineShell,
} from '../serviceWorkerRegistration';
import { logger } from './kioskLogger';

/**
 * Real reachability check. navigator.onLine only reports whether the OS has a
 * network route, not whether our server is reachable, so we also probe the
 * lightweight /api/health endpoint with a short timeout.
 */
export async function isReallyOnline(timeoutMs = 5000) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch('/api/health', { cache: 'no-store', signal: controller.signal });
    clearTimeout(timer);
    return !!(res && res.ok);
  } catch (e) {
    return false;
  }
}

/**
 * Centralized, safe alternative to window.location.reload(true).
 *
 * - When a new build is needed: ensures the new service worker has fully
 *   downloaded every asset (parked in "waiting") before activating + reloading,
 *   so the reload is served entirely from local cache and cannot fail mid-flight.
 * - When no new build is needed (date rollover, error recovery): reloads only if
 *   online, or if a controlling service worker can serve the cached shell offline.
 * - Never reloads into the browser's native offline screen, and never uses the
 *   legacy hard-reload (which can bypass the service worker).
 *
 * @param {{ reason: string, needNewBuild?: boolean }} options
 * @returns {Promise<boolean>} true if a reload was triggered, false if skipped.
 */
export async function safeReload({ reason, needNewBuild = false } = {}) {
  const online = await isReallyOnline();

  if (needNewBuild) {
    if (!online) {
      logger.warn('safeReload skipped: new build needed but offline', { reason });
      return false;
    }

    const result = await checkForUpdate(30000);

    // build_number changed but the service worker did not (e.g. only schedule
    // metadata changed). A plain reload is safe since we are online.
    if (result === 'none') {
      logger.info('safeReload: no new SW version; plain reload', { reason });
      window.location.reload();
      return true;
    }

    if (!isUpdateReady()) {
      logger.warn('safeReload skipped: new version not fully downloaded yet', { reason, result });
      return false;
    }

    // Best-effort confirmation that fresh schedule data is reachable + cached.
    await verifyFreshSchedule(8000);

    logger.info('safeReload: activating fully-downloaded update', { reason });
    await activateUpdateAndReload();
    return true;
  }

  // No new build required.
  if (online) {
    await verifyFreshSchedule(8000);
    window.location.reload();
    return true;
  }

  // Offline: only reload if the cached shell can serve it.
  if (hasOfflineShell()) {
    logger.info('safeReload: offline reload from cached shell', { reason });
    window.location.reload();
    return true;
  }

  logger.warn('safeReload skipped: offline and no cached shell', { reason });
  return false;
}
